import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import Card from "~components/shared/card/Card";
import Headline from "~components/shared/headline/Headline";
import { useTranslateMessage } from "~i18n/translate";
import { isOpinion as isOpinionStory } from "~models/transforms/eyebrow";
import { HEADLINE_SIZE } from "~types/lineup";
import type { RelatedItem } from "~types/types";

import styles from "./PhoenixRelatedItems.module.css";

interface PhoenixRelatedItemsProps {
    relatedItems: RelatedItem[];
    relatedTitle?: string;
    maxRelated?: number;
    storyIndex: number;
}

const DEFAULT_MAX_RELATED = 3;

function RelatedList(props: PhoenixRelatedItemsProps): JSX.Element {
    const translateMessage = useTranslateMessage();
    const {
        relatedItems,
        storyIndex,
        relatedTitle: incomingRelatedTitle,
    } = props;

    const relatedTitle =
        incomingRelatedTitle ??
        translateMessage({
            id: "related",
            defaultMessage: "Related",
        });

    return (
        <div
            className={styles.relatedListContainer}
            data-component="related-list"
        >
            <h3
                className={clsx(
                    styles.relatedItemTitle,
                    typographyStyles.articleLabel
                )}
            >
                {relatedTitle}
            </h3>

            <div className={styles.relatedList}>
                {relatedItems.map(
                    ({ brand, franchise, headline, id, url }, index) => {
                        const isOpinion = isOpinionStory(brand, franchise);

                        return (
                            <a
                                data-link-index={storyIndex}
                                data-link-related-type="Related"
                                data-link-related-index={index + 1}
                                href={url}
                                key={id}
                                className={styles.relatedListItem}
                            >
                                <Headline
                                    preamble={
                                        isOpinion && (
                                            <span
                                                className={styles.opinionLabel}
                                            >
                                                {`${translateMessage({
                                                    id: "opinion",
                                                    defaultMessage: "Opinion:",
                                                })}\u00A0`}
                                            </span>
                                        )
                                    }
                                    text={headline}
                                    isPhoenix
                                    size={HEADLINE_SIZE.EXTRA_SMALL}
                                />
                            </a>
                        );
                    }
                )}
            </div>
        </div>
    );
}

export default function PhoenixRelatedItems({
    relatedItems = [],
    relatedTitle,
    maxRelated = DEFAULT_MAX_RELATED,
    storyIndex,
}: PhoenixRelatedItemsProps) {
    if (!relatedItems || relatedItems.length === 0) return null;

    if (relatedItems.length === 1) {
        return (
            <Card
                relatedLinkType="Related"
                storyIndex={storyIndex}
                relatedIndex={1}
                item={relatedItems[0]}
            />
        );
    }

    return (
        <RelatedList
            storyIndex={storyIndex}
            relatedItems={relatedItems.slice(0, maxRelated)}
            relatedTitle={relatedTitle}
        />
    );
}
